/**
 * Usage Limit Modal Component
 * Shows when users hit AI feature limits with upgrade options
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Crown, Sparkles, Zap, TrendingUp } from 'lucide-react';
import { AIUsageLimits, AIFeatureType } from '../lib/aiUsageService';
import { useAIUsage } from '../hooks/useAIUsage';

interface UsageLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  limits: AIUsageLimits;
  featureType: AIFeatureType;
  onUpgrade?: (planType: 'pro' | 'premium') => void;
}

const featureDisplayNames: Record<AIFeatureType, string> = {
  image_scan: 'Photo Scanning',
  recipe_generation: 'AI Recipe Generation',
  meal_analysis: 'Meal Analysis',
  image_generation: 'Recipe Image Generation'
};

const featureIcons: Record<AIFeatureType, React.ReactNode> = {
  image_scan: <Sparkles className="w-5 h-5" />,
  recipe_generation: <Crown className="w-5 h-5" />,
  meal_analysis: <Zap className="w-5 h-5" />,
  image_generation: <TrendingUp className="w-5 h-5" />
};

export const UsageLimitModal: React.FC<UsageLimitModalProps> = ({
  isOpen,
  onClose,
  limits,
  featureType,
  onUpgrade
}) => {
  const { getUpgradeRecommendation, upgradePlan, loading } = useAIUsage();
  const recommendation = getUpgradeRecommendation(limits);
  
  const handleUpgrade = async (planType: 'pro' | 'premium') => {
    if (onUpgrade) {
      onUpgrade(planType);
    } else {
      const success = await upgradePlan(planType);
      if (success) {
        onClose();
      }
    }
  };

  // Handle null/undefined limits gracefully
  if (!limits) {
    return null;
  }

  const resetDate = new Date(limits.resets_on).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-full bg-orange-100 dark:bg-orange-900 text-orange-600 dark:text-orange-400">
                  {featureIcons[featureType]}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Feature Limit Reached
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {featureDisplayNames[featureType]}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Usage Stats */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Monthly Usage
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {limits.plan_type.charAt(0).toUpperCase() + limits.plan_type.slice(1)} Plan
                  </span>
                </div>
                
                <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2 mb-2">
                  <div
                    className="bg-orange-500 h-2 rounded-full"
                    style={{ width: '100%' }}
                  />
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">
                    {limits.used} / {limits.limit} used
                  </span>
                  <span className="text-gray-600 dark:text-gray-400">
                    Resets {resetDate}
                  </span>
                </div>
              </div>

              {/* Limit Message */}
              <div className="text-center">
                <p className="text-gray-700 dark:text-gray-300 mb-2">
                  {limits.reason}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Your {limits.plan_type} plan includes {limits.limit} {featureDisplayNames[featureType].toLowerCase()} requests per month.
                </p>
              </div>

              {/* Upgrade Options */}
              {limits.plan_type === 'free' && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-900 dark:text-white text-center">
                    Upgrade for More AI Features
                  </h4>
                  
                  {/* Pro Plan */}
                  <div className="border border-primary rounded-xl p-4 relative">
                    <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                      <span className="bg-primary text-white px-3 py-1 rounded-full text-xs font-medium">
                        Recommended
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h5 className="font-semibold text-gray-900 dark:text-white">Pro Plan</h5>
                        <p className="text-2xl font-bold text-primary">$15<span className="text-sm font-normal text-gray-500">/month</span></p>
                      </div>
                      <Crown className="w-8 h-8 text-primary" />
                    </div>
                    
                    <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400 mb-4">
                      <li>• Full-featured inventory tracking</li>
                      <li>• Unlimited photo/receipt scanning</li>
                      <li>• AI recipe generation (200/month)</li>
                      <li>• Unlimited meal logs</li>
                      <li>• Full-featured meal planning</li>
                      <li>• Personalized calorie tracker</li>
                    </ul>
                    
                    <button
                      onClick={() => handleUpgrade('pro')}
                      disabled={loading}
                      className="w-full bg-primary text-white py-2 px-4 rounded-lg font-medium hover:bg-primary-dark transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Processing...' : 'Upgrade to Pro'}
                    </button>
                  </div>
                  
                  {/* Premium Plan */}
                  <div className="border border-gray-200 dark:border-gray-600 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h5 className="font-semibold text-gray-900 dark:text-white">Premium Plan</h5>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">$19.99<span className="text-sm font-normal text-gray-500">/month</span></p>
                      </div>
                      <Sparkles className="w-8 h-8 text-purple-600" />
                    </div>
                    
                    <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400 mb-4">
                      <li>• 1000 AI requests/month</li>
                      <li>• Everything in Pro</li>
                      <li>• Data export</li>
                      <li>• Family sharing</li>
                      <li>• Priority support</li>
                    </ul>
                    
                    <button
                      onClick={() => handleUpgrade('premium')}
                      disabled={loading}
                      className="w-full bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Processing...' : 'Upgrade to Premium'}
                    </button>
                  </div>
                </div>
              )}

              {/* Pro Plan Upgrade to Premium */}
              {limits.plan_type === 'pro' && recommendation.shouldUpgrade && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-900 dark:text-white text-center">
                    Need More AI Requests?
                  </h4>
                  
                  <div className="border border-purple-200 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h5 className="font-semibold text-gray-900 dark:text-white">Premium Plan</h5>
                        <p className="text-2xl font-bold text-purple-600">$19.99<span className="text-sm font-normal text-gray-500">/month</span></p>
                      </div>
                      <Sparkles className="w-8 h-8 text-purple-600" />
                    </div>
                    
                    <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400 mb-4">
                      <li>• 1000 AI requests/month (5x more!)</li>
                      <li>• Everything in Pro</li>
                      <li>• Data export capabilities</li>
                      <li>• Family sharing for up to 6 members</li>
                      <li>• Priority customer support</li>
                    </ul>
                    
                    <button
                      onClick={() => handleUpgrade('premium')}
                      disabled={loading}
                      className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Processing...' : 'Upgrade to Premium'}
                    </button>
                  </div>
                </div>
              )}

              {/* Alternative Actions */}
              <div className="text-center space-y-2">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Or continue with manual entry until your limit resets
                </p>
                <button
                  onClick={onClose}
                  className="text-primary hover:text-primary-dark font-medium text-sm"
                >
                  Continue with Manual Entry
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
